import { useMemo, useRef, useState, useEffect } from 'react';
import { InventoryAsset } from '../../../types';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, MarkerClusterer } from '@react-google-maps/api';
import { Icon } from '../../Atoms/Icon';
import { FilterBar } from './FilterBar';
import { InfoWindowContent } from './InfoWindowContent';
import { useNavigate } from 'react-router-dom';
import mapStyles from './mapStyles.json';
import {
  FILTER_SERVICE_CATEGORIES,
  generateALocationMapData,
  generateMapData,
  generateZLocationMapData,
  coordKey
} from '../../../utils/helpers';
import { CollapseButton } from '../../Atoms/CollapseButton';
import { MapTable } from './MapTable';
import { BORDER_STYLE } from '../../../utils/styleHelpers';
import { getInventoryLoadingSubject } from '../../../store/Inventory';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { TextBlock } from '../../Atoms/Text';
import { GridFilterItem, GridLogicOperator } from '@mui/x-data-grid-pro';
import { buildAddressFilterItems, buildZLocationFilterItems, buildALocationFilterItems } from './mapFilterHelpers';
import { getConfiguration } from '../../../store/Configuration';
import { withGoogleMapsApiKey } from '../../../utils/withGoogleMapsApiKey';

const center = {
  lat: 42,
  lng: -100
};

export interface MapWidgetProps {
  inventoryAssets: InventoryAsset[];
  isHome?: boolean;
  isOnlyFull?: boolean;
  handleFullClose?: () => void;
  filterInventoryAssets: (filters: GridFilterItem[], logicOperator?: GridLogicOperator) => void;
}

function MapWidgetContent({
  inventoryAssets,
  isOnlyFull,
  handleFullClose,
  isHome,
  filterInventoryAssets
}: MapWidgetProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [loadedMarkers, setLoadedMarkers] = useState<number>(0);
  const [isFullsize, setIsFullsize] = useState<boolean>(isOnlyFull || false);
  const [selectedAddress, setSelectedAddress] = useState<[string, InventoryAsset[]] | null>(null);
  const [selectedZAddress, setSelectedZAddress] = useState<[string, InventoryAsset[]] | null>(null);
  const [selectedAAddress, setSelectedAAddress] = useState<[string, InventoryAsset[]] | null>(null);
  const [selectedProductNames, setSelectedProductNames] = useState<string[]>([]);
  const [collapse, setCollapse] = useState<boolean>(false);
  const navigate = useNavigate();
  const seenProductNamesRef = useRef(new Set<string>());

  // Build family → product names map from current inventory (memoized to stabilize the effect dependency)
  const productsByFamily = useMemo(() => {
    const map = new Map<string, string[]>();
    inventoryAssets.forEach((asset) => {
      const family = asset.product?.family;
      const name = asset.product?.name;
      if (family && name && FILTER_SERVICE_CATEGORIES.includes(family)) {
        if (!map.has(family)) map.set(family, []);
        const names = map.get(family)!;
        if (!names.includes(name)) names.push(name);
      }
    });
    return map;
  }, [inventoryAssets]);

  const productFamilies = new Set(productsByFamily.keys());
  const totalProductNames = Array.from(productsByFamily.values()).reduce((sum, names) => sum + names.length, 0);
  const isShowFilterBar = productFamilies.size > 1 || totalProductNames > 1;

  // Auto-select newly discovered product names (on by default)
  useEffect(() => {
    const newNames: string[] = [];
    productsByFamily.forEach((names) => {
      names.forEach((name) => {
        if (!seenProductNamesRef.current.has(name)) {
          seenProductNamesRef.current.add(name);
          newNames.push(name);
        }
      });
    });
    if (newNames.length > 0) {
      setSelectedProductNames((prev) => [...prev, ...newNames]);
    }
  }, [productsByFamily]);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: getConfiguration().REACT_APP_GOOGLE_MAPS_API_KEY });
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadingSub = getInventoryLoadingSubject().subscribe((loading) => setInventoryLoading(loading));

    return () => {
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, []);

  const rawAddresses = useMemo(
    () => generateMapData(inventoryAssets, selectedProductNames),
    [inventoryAssets, selectedProductNames]
  );

  const rawZAddresses = useMemo(
    () => generateZLocationMapData(inventoryAssets, selectedProductNames),
    [inventoryAssets, selectedProductNames]
  );

  // Merge Z location groups into the install-address marker when they share the same coordinates,
  // so overlapping dots collapse into a single marker showing all circuits at that location.
  const { addresses, zAddresses } = useMemo(() => {
    const addressCoordMap = new Map<string, string>(); // coordKey → address label
    for (const [addr, assets] of rawAddresses) {
      const lat = assets[0]?.addressInfo?.latitude;
      const lng = assets[0]?.addressInfo?.longitude;
      if (lat && lng) addressCoordMap.set(coordKey(lat, lng), addr);
    }

    const mergedExtra = new Map<string, InventoryAsset[]>(); // address label → extra Z assets
    const remainingZ: [string, InventoryAsset[]][] = [];
    for (const [zAddr, zAssets] of rawZAddresses) {
      const asset = zAssets[0];
      const zLat = asset?.locationZLatitude;
      const zLng = asset?.locationZLongitude;
      if (zLat && zLng) {
        const key = coordKey(zLat, zLng);
        const matchedAddr = addressCoordMap.get(key);
        if (matchedAddr) {
          mergedExtra.set(matchedAddr, [...(mergedExtra.get(matchedAddr) ?? []), ...zAssets]);
          continue;
        }
      }
      remainingZ.push([zAddr, zAssets]);
    }

    const mergedAddresses = rawAddresses.map(([addr, assets]) => {
      const extra = mergedExtra.get(addr);
      return extra
        ? ([addr, [...assets, ...extra]] as [string, InventoryAsset[]])
        : ([addr, assets] as [string, InventoryAsset[]]);
    });

    return { addresses: mergedAddresses, zAddresses: remainingZ };
  }, [rawAddresses, rawZAddresses]);

  const aAddresses = useMemo(
    () => generateALocationMapData(inventoryAssets, selectedProductNames),
    [inventoryAssets, selectedProductNames]
  );

  useEffect(() => {
    setLoadedMarkers(0);
  }, [rawAddresses, rawZAddresses, aAddresses]);

  const onLoadMarker = () => {
    setLoadedMarkers((prev) => prev + 1);
  };

  const handleMarkerClick = (assets: InventoryAsset[]) => {
    setSelectedZAddress(null);
    setSelectedAAddress(null);
    setSelectedAddress([assets?.[0]?.addressInfo?.locationName, assets]);
    if (!isFullsize) setIsFullsize(true);
  };

  const handleZMarkerClick = (assets: InventoryAsset[]) => {
    setSelectedAddress(null);
    setSelectedAAddress(null);
    setSelectedZAddress([assets?.[0]?.locationZText, assets]);
    if (!isFullsize) setIsFullsize(true);
  };

  const handleAMarkerClick = (assets: InventoryAsset[]) => {
    setSelectedAddress(null);
    setSelectedZAddress(null);
    setSelectedAAddress([assets?.[0]?.locationAText, assets]);
    if (!isFullsize) setIsFullsize(true);
  };

  const markers = useMemo(
    () =>
      addresses.map((address, index) => {
        return (
          <Marker
            key={`${address?.[1]?.[0]?.addressInfo?.latitude}-${address?.[1]?.[0]?.addressInfo?.longitude}-${index}`}
            onClick={() => handleMarkerClick(address[1])}
            onLoad={onLoadMarker}
            position={{ lat: address?.[1]?.[0]?.addressInfo?.latitude, lng: address?.[1]?.[0]?.addressInfo?.longitude }}
          />
        );
      }),
    [addresses]
  );

  const zMarkers = useMemo(
    () =>
      zAddresses.map((zAddress, index) => {
        const asset = zAddress[1][0];
        return (
          <Marker
            key={`z-${asset?.locationZLatitude}-${asset?.locationZLongitude}-${index}`}
            onClick={() => handleZMarkerClick(zAddress[1])}
            onLoad={onLoadMarker}
            position={{ lat: asset?.locationZLatitude || 0, lng: asset?.locationZLongitude || 0 }}
          />
        );
      }),
    [zAddresses]
  );

  const aMarkers = useMemo(
    () =>
      aAddresses.map((aAddress, index) => {
        const asset = aAddress[1][0];
        return (
          <Marker
            key={`a-${asset?.locationALatitude}-${asset?.locationALongitude}-${index}`}
            onClick={() => handleAMarkerClick(aAddress[1])}
            onLoad={onLoadMarker}
            position={{ lat: asset?.locationALatitude || 0, lng: asset?.locationALongitude || 0 }}
          />
        );
      }),
    [aAddresses]
  );

  useEffect(() => {
    if (
      (!isFullsize || isOnlyFull) &&
      loadedMarkers === addresses.length + zAddresses.length + aAddresses.length &&
      (addresses.length > 0 || zAddresses.length > 0 || aAddresses.length > 0) &&
      window.google.maps
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        const position = marker.props.position;
        bounds.extend(new window.google.maps.LatLng(position?.lat, position?.lng));
      });
      zMarkers.forEach((marker) => {
        const position = marker.props.position;
        bounds.extend(new window.google.maps.LatLng(position?.lat, position?.lng));
      });
      aMarkers.forEach((marker) => {
        const position = marker.props.position;
        bounds.extend(new window.google.maps.LatLng(position?.lat, position?.lng));
      });
      mapRef.current?.fitBounds(bounds);
    }
  }, [loadedMarkers, addresses, zAddresses, aAddresses, markers, zMarkers, aMarkers]);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const isInventory = document.getElementsByClassName('masonry-item').length == 0;
  const checkMapWidth = isInventory ? '300px' : 'auto';
  const checkMapHeight = isInventory ? '175px' : '350px';
  const mapButtons = isInventory ? 'top-[140px]' : 'top-[310px]';

  const mapcontainer = () => {
    return (
      <GoogleMap
        mapContainerStyle={{
          width: isFullsize ? '100%' : checkMapWidth,
          height: isFullsize ? '100%' : checkMapHeight
        }}
        options={{
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          styles: mapStyles,
          zoomControl: false
        }}
        center={center}
        onLoad={handleMapLoad}
        mapContainerClassName={isFullsize ? 'rounded-lg' : collapse ? '' : 'rounded-b-lg'}
        zoom={3}>
        <MarkerClusterer options={{ imagePath: '/cluster', maxZoom: 15 }}>
          {(clusterer) => (
            <>
              {markers.map((marker) => (
                <Marker
                  icon="/circle.svg"
                  clusterer={clusterer}
                  key={marker.key}
                  {...marker.props}
                  onLoad={onLoadMarker}
                />
              ))}
              {zMarkers.map((marker) => (
                <Marker
                  icon="/circle-z.svg"
                  clusterer={clusterer}
                  key={marker.key}
                  {...marker.props}
                  onLoad={onLoadMarker}
                />
              ))}
              {aMarkers.map((marker) => (
                <Marker
                  icon="/circle.svg"
                  clusterer={clusterer}
                  key={marker.key}
                  {...marker.props}
                  onLoad={onLoadMarker}
                />
              ))}
            </>
          )}
        </MarkerClusterer>
        {isFullsize && selectedAddress && (
          <InfoWindow
            position={{
              lat: selectedAddress?.[1]?.[0]?.addressInfo?.latitude,
              lng: selectedAddress?.[1]?.[0]?.addressInfo?.longitude
            }}
            options={{ pixelOffset: new google.maps.Size(0, -15) }}
            onCloseClick={() => setSelectedAddress(null)}>
            <InfoWindowContent
              inventoryAssets={selectedAddress[1]}
              onClick={addressClick(
                selectedAddress[1],
                selectedAddress[1]?.[0]?.addressInfo?.latitude,
                selectedAddress[1]?.[0]?.addressInfo?.longitude
              )}
            />
          </InfoWindow>
        )}
        {isFullsize && selectedZAddress && (
          <InfoWindow
            position={{
              lat: selectedZAddress?.[1]?.[0]?.locationZLatitude || 0,
              lng: selectedZAddress?.[1]?.[0]?.locationZLongitude || 0
            }}
            options={{ pixelOffset: new google.maps.Size(0, -15) }}
            onCloseClick={() => setSelectedZAddress(null)}>
            <InfoWindowContent
              inventoryAssets={selectedZAddress[1]}
              locationName={selectedZAddress[1]?.[0]?.locationZName}
              address={selectedZAddress[1]?.[0]?.locationZText}
              onClick={zAddressClick(selectedZAddress[1])}
            />
          </InfoWindow>
        )}
        {isFullsize && selectedAAddress && (
          <InfoWindow
            position={{
              lat: selectedAAddress?.[1]?.[0]?.locationALatitude || 0,
              lng: selectedAAddress?.[1]?.[0]?.locationALongitude || 0
            }}
            options={{ pixelOffset: new google.maps.Size(0, -15) }}
            onCloseClick={() => setSelectedAAddress(null)}>
            <InfoWindowContent
              inventoryAssets={selectedAAddress[1]}
              locationName={selectedAAddress[1]?.[0]?.locationAName}
              address={selectedAAddress[1]?.[0]?.locationAText}
              onClick={aAddressClick(selectedAAddress[1])}
            />
          </InfoWindow>
        )}
      </GoogleMap>
    );
  };

  const handleIsFullsize = () => {
    mapRef.current = null;
    setSelectedAddress(null);
    setSelectedZAddress(null);
    setSelectedAAddress(null);
    isOnlyFull && handleFullClose ? handleFullClose() : setIsFullsize(!isFullsize);
  };

  const handleZoom = (diff: number) => () => {
    mapRef.current?.setZoom((mapRef.current?.getZoom() || 0) + diff);
  };

  const handleFamilyToggle = (family: string, productNames: string[]) => {
    const allSelected = productNames.every((n) => selectedProductNames.includes(n));
    setSelectedProductNames((prev) =>
      allSelected ? prev.filter((n) => !productNames.includes(n)) : [...new Set([...prev, ...productNames])]
    );
    setSelectedAddress(null);
    setSelectedZAddress(null);
    setSelectedAAddress(null);
  };

  const handleProductToggle = (productName: string) => {
    setSelectedProductNames((prev) =>
      prev.includes(productName) ? prev.filter((n) => n !== productName) : [...prev, productName]
    );
    setSelectedAddress(null);
    setSelectedZAddress(null);
    setSelectedAAddress(null);
  };

  const addressClick = (assets: InventoryAsset[], pinLat: number | undefined, pinLng: number | undefined) => () => {
    if (pinLat == null || pinLng == null) return;
    handleIsFullsize();
    const filters = buildAddressFilterItems(assets, pinLat, pinLng);
    if (filters.length > 0) filterInventoryAssets(filters, GridLogicOperator.Or);
  };

  const zAddressClick = (assets: InventoryAsset[]) => () => {
    handleIsFullsize();
    const filters = buildZLocationFilterItems(assets.map((a) => a.locationZText).filter(Boolean) as string[]);
    if (filters.length > 0) filterInventoryAssets(filters, GridLogicOperator.Or);
  };

  const aAddressClick = (assets: InventoryAsset[]) => () => {
    handleIsFullsize();
    const filters = buildALocationFilterItems(assets.map((a) => a.locationAText).filter(Boolean) as string[]);
    if (filters.length > 0) filterInventoryAssets(filters, GridLogicOperator.Or);
  };

  const handleAllClick = () => {
    navigate('/inventory');
  };

  return (
    <div className={`${BORDER_STYLE} pt-2 min-w-64 bg-white`}>
      <div className="col-span-2 text-sm font-medium text-grey-5 text-center">Locations</div>
      {inventoryLoading && (
        <div className={`flex ${isInventory ? 'h-44' : 'h-32'} items-center justify-center`}>
          <ComponentSpinner />
        </div>
      )}
      {!inventoryLoading && !inventoryAssets?.length && (
        <div className={`flex ${isInventory ? 'h-44' : 'h-32'} items-center justify-center`}>
          <TextBlock size="sm14">No data available</TextBlock>
        </div>
      )}
      {!inventoryLoading && inventoryAssets?.length > 0 && (
        <div
          className="pt-2 relative text-[10px] map-container"
          data-cy="map-container"
          data-testid="map-container">
          {isFullsize && (
            <div
              className="fixed top-[3%] left-[3%] w-[94%] h-[94%] z-10"
              data-testid="map-fullsize">
              <button
                aria-label="map-close-button"
                className="h-[40px] w-[40px] border-[1px] border-gray-3 rounded-[5px] p-[11px] bg-white absolute right-[17px] top-[17px] z-10"
                data-cy="map-close-button"
                onClick={handleIsFullsize}>
                <Icon
                  type="cancelDark"
                  className="cursor-pointer h-[16px] !bg-contain"
                />
              </button>
              <button
                aria-label="map-zoom-plus-button"
                className="h-[40px] w-[40px] border-[1px] border-gray-3 rounded-[5px] p-[11px] bg-white absolute right-[17px] bottom-[62px] z-10"
                onClick={handleZoom(1)}>
                <Icon
                  type="plusRectangle"
                  className="cursor-pointer h-[16px] !bg-contain"
                />
              </button>
              <button
                aria-label="map-zoom-minus-button"
                className="h-[40px] w-[40px] border-[1px] border-gray-3 rounded-[5px] p-[11px] bg-white absolute right-[17px] bottom-[17px] z-10"
                onClick={handleZoom(-1)}>
                <Icon
                  type="minus"
                  className="cursor-pointer h-[16px] !bg-contain"
                />
              </button>
              {isShowFilterBar && (
                <FilterBar
                  productsByFamily={productsByFamily}
                  selectedProductNames={selectedProductNames}
                  onFamilyToggle={handleFamilyToggle}
                  onProductToggle={handleProductToggle}
                />
              )}
              <div className="fixed w-[100%] h-[100%] top-0 left-0 bg-black opacity-50 z-0" />
              {isLoaded && mapcontainer()}
            </div>
          )}
          {!isFullsize && (
            <>
              {isLoaded && mapcontainer()}
              <button
                style={{ right: isHome ? 40 : 10 }}
                // eslint-disable-next-line max-len
                className={`h-[25px] w-[25px] border-[1px] border-gray-3 rounded-1 p-1.5 bg-white absolute ${mapButtons}`}
                onClick={handleIsFullsize}
                aria-label="map-fullsize-button"
                data-cy="map-fullsize-button">
                <Icon
                  type="expand"
                  className="fill-indigo cursor-pointer h-[11px]"
                />
              </button>
              {isHome && (
                <div
                  className={`h-[25px] w-[25px] border-[1px] border-gray-3 rounded-1 p-[8.5px] bg-white absolute
                right-2.5 ${mapButtons}`}>
                  <CollapseButton
                    classNames=""
                    testId="mapWidget"
                    collapse={collapse}
                    setCollapse={setCollapse}
                  />
                </div>
              )}
              {isHome && collapse && (
                <MapTable
                  handleAllClick={handleAllClick}
                  inventoryAssets={inventoryAssets}
                  checkMapWidth={checkMapWidth}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export const MapWidget = withGoogleMapsApiKey(MapWidgetContent);
